/** @jsxImportSource @emotion/react */

import { useCallback, useMemo, useRef, useState } from 'react'
import { Line } from 'react-chartjs-2'
import {
  ChartData,
  ChartDataset,
  Chart as ChartJs,
  ChartOptions,
  Chart as ChartType,
  Plugin,
  Point,
  ScriptableContext,
  ScriptableScaleContext,
  TooltipModel,
  registerables,
} from 'chart.js'
import { Interpolation, Theme, useTheme } from '@emotion/react'
import { ChartJSOrUndefined } from 'react-chartjs-2/dist/types'
import merge from 'lodash/merge'
import { uniq } from 'lodash'
import { Position, ChartTooltip } from 'components/ChartTooltip'
import { COURSE_COLORS, CourseName } from 'theme/utils'
import * as classes from './styles'

ChartJs.register(...registerables)

type Props<
  T extends {
    xAxisValue: number | string
    yAxisValue: number | string
  }
> = {
  data: T[]
  subject: CourseName
  className?: string
  isPastPracticeTest?: boolean
  chartOptions?: ChartOptions<'line'>
  datasets?: ChartDataset<'line', T[]>[]
  datasetOptions?: Omit<ChartDataset<'line', T>, 'data'>
  tooltipStyles?: Interpolation<Theme>
  plugins?: Plugin[]
  hideTooltipTriangle?: boolean
  renderTooltipContent: (pointData: T) => React.ReactNode
}

export const BORDER_COLOR = '#2E2E2E'
export const GRID_COLOR = '#3C3C3C'

const CHART_MAX_WIDTH = 300

export const Chart = <
  T extends {
    xAxisValue: number | string
    yAxisValue: number | string
  }
>({
  data: dataProp,
  subject,
  datasets,
  plugins,
  className,
  isPastPracticeTest,
  chartOptions,
  datasetOptions,
  tooltipStyles,
  hideTooltipTriangle,
  renderTooltipContent,
}: Props<T>) => {
  const [tooltipVisible, setTooltipVisible] = useState(false)
  const [tooltipData, setTooltipData] = useState<TooltipModel<'line'> | null>(null)
  const [tooltipPosition, setTooltipPosition] = useState<Position | null>(null)
  const chartRef = useRef<ChartJSOrUndefined<'line', T[]>>(null)
  const theme = useTheme()

  const data: ChartData<'line', T[]> = useMemo(() => {
    const defaultDataSetProps: Omit<ChartDataset<'line', Point[]>, 'data'> = {
      label: '',
      fill: true,
      cubicInterpolationMode: 'monotone',
      borderColor: COURSE_COLORS[subject].main,
      pointBackgroundColor: COURSE_COLORS[subject].main,
      pointBorderColor: COURSE_COLORS[subject].light,
      pointRadius: 5,
      pointBorderWidth: 6,
      pointHoverRadius: 5,
      pointHoverBorderWidth: 6,
      backgroundColor: (context: ScriptableContext<'line'>) => {
        const ctx = context.chart.ctx
        const gradient = ctx.createLinearGradient(0, 0, 0, 150)
        gradient.addColorStop(0, COURSE_COLORS[subject].light)
        gradient.addColorStop(1, COURSE_COLORS[subject].opacity)
        return gradient
      },
    }

    return {
      labels: datasets
        ? uniq(
            datasets.reduce(
              (acc, dataset) => [...acc, ...dataset.data.map(({ xAxisValue }) => xAxisValue)],
              [] as (string | number)[]
            )
          )
        : dataProp.map(({ xAxisValue }) => xAxisValue),
      datasets: datasets
        ? datasets.map((dataset) => ({
            ...defaultDataSetProps,
            ...dataset,
          }))
        : [
            {
              data: dataProp,
              ...defaultDataSetProps,
              ...(datasetOptions ? datasetOptions : {}),
            },
          ],
    }
  }, [dataProp])

  const customTooltip = useCallback((context: { chart: ChartType; tooltip: TooltipModel<'line'> }) => {
    if (context.tooltip.opacity == 0) {
      setTooltipVisible(false)
      return
    }

    const chart = chartRef.current
    if (!chart) {
      return
    }

    const canvas = chart.canvas
    if (canvas) {
      setTooltipVisible(true)

      const { caretX, caretY } = context.tooltip

      const width = context.chart.width

      if (tooltipPosition?.top !== caretY) {
        const displayToTheLeft = caretX + CHART_MAX_WIDTH > width

        const leftOffset = 20
        setTooltipPosition({
          top: caretY,
          left: caretX + leftOffset,
          displayToTheLeft,
        })
        setTooltipData(context.tooltip)
      }
    }
  }, [])

  const options = useMemo(() => {
    const defaultOptions: ChartOptions<'line'> = {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            color: theme.colors.typographyTwo,
            count: 5,
            font: {
              size: 15,
              weight: 'normal',
            },
            callback: (tickValue: number | string) => `${tickValue}%`,
          },
          suggestedMin: 0,
          suggestedMax: 100,
          grid: {
            color: (context: ScriptableScaleContext) => {
              if (context.tick?.value === 0) {
                return theme.backgrounds.backgroundOne
              }

              return theme.backgrounds.backgroundOne
            },
          },
          border: {
            dash: [7, 6],
            width: 0,
            dashOffset: 7,
          },
        },
        x: {
          ticks: {
            color: theme.colors.typographyTwo,
            font: {
              size: 14,
            },
          },
          grid: {
            display: false,
          },
          border: {
            color: theme.backgrounds.backgroundOne,
          },
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: false,
          external: customTooltip,
          xAlign: 'left',
          yAlign: 'center',
        },
      },
      parsing: {
        xAxisKey: 'xAxisValue',
        yAxisKey: 'yAxisValue',
      },
    }

    return chartOptions ? merge(defaultOptions, chartOptions) : defaultOptions
  }, [chartOptions, customTooltip])

  return (
    <div css={classes.chart} className={className}>
      <Line data={data} options={options} ref={chartRef} plugins={plugins} />
      <ChartTooltip<T>
        isVisible={tooltipVisible}
        subject={subject}
        css={tooltipStyles}
        tooltipData={tooltipData}
        data={dataProp}
        position={tooltipPosition}
        isPastPracticeTest={isPastPracticeTest}
        hideTooltipTriangle={hideTooltipTriangle}
        renderTooltipContent={renderTooltipContent}
      />
    </div>
  )
}
